import { useState, useEffect } from 'react';
import { logApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  FileClock,
  AlertCircle,
  Info,
  AlertTriangle,
  XCircle,
  Terminal,
  Clock,
} from 'lucide-react';

interface SystemLog {
  id: string;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  category: string;
  message: string;
  details?: Record<string, unknown>;
  createdAt: string;
}

export function LogsPage() {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [level, setLevel] = useState('');
  const [category, setCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const response = await logApi.listLogs({
        page,
        pageSize,
        level: level || undefined,
        category: category || undefined,
      });
      if (response.code === 0) {
        setLogs(response.data.items as SystemLog[]);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, pageSize, level, category]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('zh-CN');
  };

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'debug':
        return <Badge variant="default">DEBUG</Badge>;
      case 'info':
        return <Badge variant="info">INFO</Badge>;
      case 'warn':
        return <Badge variant="warning">WARN</Badge>;
      case 'error':
        return <Badge variant="error">ERROR</Badge>;
      case 'fatal':
        return <Badge variant="error">FATAL</Badge>;
      default:
        return <Badge>{level}</Badge>;
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'debug':
        return <Terminal className="w-4 h-4 text-zinc-400" />;
      case 'info':
        return <Info className="w-4 h-4 text-blue-500" />;
      case 'warn':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-error-500" />;
      case 'fatal':
        return <XCircle className="w-4 h-4 text-error-600" />;
      default:
        return <Info className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      system: '系统',
      database: '数据库',
      ai: 'AI',
      file: '文件',
      auth: '认证',
      workflow: '工作流',
    };
    return labels[category] || category;
  };

  const errorCount = logs.filter(l => l.level === 'error' || l.level === 'fatal').length;
  const warnCount = logs.filter(l => l.level === 'warn').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">系统日志</h1>
        <p className="text-zinc-500 mt-1">查看系统运行日志</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总日志数</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">{total}</p>
              </div>
              <div className="p-3 bg-accent-100 rounded-xl">
                <FileClock className="w-6 h-6 text-accent-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">错误数</p>
                <p className="text-2xl font-bold text-error-600 mt-1">{errorCount}</p>
              </div>
              <div className="p-3 bg-error-100 rounded-xl">
                <XCircle className="w-6 h-6 text-error-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">警告数</p>
                <p className="text-2xl font-bold text-warning-600 mt-1">{warnCount}</p>
              </div>
              <div className="p-3 bg-warning-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-warning-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">正常率</p>
                <p className="text-2xl font-bold text-success-600 mt-1">
                  {logs.length > 0 ? ((logs.length - errorCount - warnCount) / logs.length * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className="p-3 bg-success-100 rounded-xl">
                <Info className="w-6 h-6 text-success-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
            >
              <option value="">所有级别</option>
              <option value="debug">DEBUG</option>
              <option value="info">INFO</option>
              <option value="warn">WARN</option>
              <option value="error">ERROR</option>
              <option value="fatal">FATAL</option>
            </select>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
            >
              <option value="">所有分类</option>
              <option value="system">系统</option>
              <option value="database">数据库</option>
              <option value="ai">AI</option>
              <option value="file">文件</option>
              <option value="auth">认证</option>
              <option value="workflow">工作流</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>日志列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>级别</TableHead>
                    <TableHead>分类</TableHead>
                    <TableHead>消息</TableHead>
                    <TableHead>时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getLevelIcon(log.level)}
                          {getLevelBadge(log.level)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="default">{getCategoryLabel(log.category)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-zinc-900">{log.message}</p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <pre className="mt-2 text-xs bg-zinc-100 rounded p-2 overflow-x-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-zinc-400" />
                          <span className="text-sm">{formatDate(log.createdAt)}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
